import { RankDetail } from '../../../../apis/ranking/ranking-api.def.ts'
import {
  ColDef,
  ColumnEvent,
  ColumnResizedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { useMemo, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { OVERALL_RANKING_COLUMN_STATE_KEY } from '../../../../utils/constants.ts'
import { BookNameCellRenderer } from '../../../components/book-name-cell-renderer.tsx'
import {
  coloredNumberCellStyle,
  currencyValueFormatter,
  percentageValueFormatter,
} from '../../../../utils/formatters.ts'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectRankingInput } from '../../../../redux/slices/ranking-input.slice.ts'
import { RankColumn } from './rank-column.tsx'
import {
  getOrderedColumnsFromState,
  isPersistableColumnResize,
  isProgrammaticColumnEvent,
} from '../../../../utils/ag-grid-table-helpers.ts'
import { useSelector } from 'react-redux'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { TotalPnlDependentHeaderComponent } from './total-pnl-dependent-header.tsx'
import { ReturnPctDependentHeaderComponent } from './return-pct-dependent-header.tsx'
import { ROLE_ADMIN, ROLE_MASTER } from '../../../../router/roles.ts'

interface OverallRankingTableProps {
  isLoading: boolean
  isFetching: boolean
  rankingList: RankDetail[]
  groupByFirm: boolean
  height: number
}

export const OverallRankingTable: React.FC<OverallRankingTableProps> = ({
  isLoading,
  isFetching,
  rankingList,
  groupByFirm,
  height,
}) => {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact>(null)
  const rankingInput = useAppSelector(selectRankingInput)
  const user = useSelector(selectUser)
  const isAdmin = user.role === ROLE_ADMIN || user.role === ROLE_MASTER
  const showTotalPnlTooltip =
    !isAdmin &&
    (rankingList.find((r) => r.bookId === user.bookId)?.avgDeployment ?? 0) > 1

  // Use different storage keys for different modes
  const columnStateKey = groupByFirm
    ? `${OVERALL_RANKING_COLUMN_STATE_KEY}_firm`
    : `${OVERALL_RANKING_COLUMN_STATE_KEY}_book`

  const applyLocalStorageColumnState = () => {
    const savedState = localStorage.getItem(columnStateKey)
    if (savedState) {
      const state = JSON.parse(savedState)

      gridRef.current?.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }

  const columns = useMemo<ColDef[]>(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: t('overall_ranking.ranking_table.columns.rank'),
        field: 'rank',
        cellRenderer: RankColumn,
        cellRendererParams: { groupByFirm },
        type: 'textColumn',
      },
      {
        headerName: groupByFirm
          ? t('overall_ranking.ranking_table.columns.firm_name')
          : t('overall_ranking.ranking_table.columns.book_name'),
        field: groupByFirm ? 'firmName' : 'bookName',
        type: 'textColumn',
        cellRenderer: BookNameCellRenderer,
        cellRendererParams: {
          dateRange: rankingInput.dateRange,
          groupByFirm,
        },
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.ideas_count'),
        field: 'ideasCount',
        type: 'numberColumn',
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.new_ideas_count'),
        field: 'newIdeasCount',
        type: 'numberColumn',
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.daily_pnl'),
        field: 'adjustedDailyPnl',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.total_pnl'),
        field: 'adjustedPnl',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
        ...(showTotalPnlTooltip && {
          headerComponent: TotalPnlDependentHeaderComponent,
        }),
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.return_pct'),
        field: 'returnPct',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: percentageValueFormatter,
        headerComponent: ReturnPctDependentHeaderComponent,
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.category'),
        field: 'category',
        type: 'textColumn',
      },
    ]

    // If you want to add Avg PnL column for groupByFirm
    if (groupByFirm) {
      const totalPnlIndex = baseColumns.findIndex(
        (col) => col.field === 'adjustedPnl'
      )
      baseColumns.splice(totalPnlIndex, 0, {
        headerName: t('overall_ranking.ranking_table.columns.avg_pnl'),
        field: 'averageAdjustedPnl',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      })
    }

    // Score is only meaningful for individual books, so hide it when grouping by firm.
    if (!groupByFirm) {
      baseColumns.push({
        headerName: t('overall_ranking.ranking_table.columns.score'),
        field: 'score',
        type: 'numberColumn',
        // score is a pre-formatted string; keep it as-is instead of numberColumn's numeric formatter.
        valueFormatter: (params) => params.value,
      })
    }

    // If you want to add a separate Firm Name column when not grouping by firm
    if (!groupByFirm) {
      const exists = baseColumns.some((col) => col.field === 'firmName')
      if (!exists) {
        baseColumns.push({
          headerName: t('overall_ranking.ranking_table.columns.firm_name'),
          field: 'firmName',
          type: 'textColumn',
        })
      }
    }

    const savedState = localStorage.getItem(columnStateKey)
    const orderedColumns = getOrderedColumnsFromState(
      savedState,
      baseColumns,
      gridRef?.current?.api
    )
    if (orderedColumns) return orderedColumns

    return baseColumns
  }, [t, groupByFirm, rankingInput, columnStateKey, isFetching, isLoading])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: false,
      initialWidth: 200,
      mainMenuItems: (params) => {
        return params.defaultItems.map((item) => {
          if (item === 'resetColumns') {
            return {
              name: 'Reset Columns',
              action: () => {
                // Remove both firm and book column state keys if they exist
                const firmKey = `${OVERALL_RANKING_COLUMN_STATE_KEY}_firm`
                const bookKey = `${OVERALL_RANKING_COLUMN_STATE_KEY}_book`

                if (localStorage.getItem(firmKey)) {
                  localStorage.removeItem(firmKey)
                }
                if (localStorage.getItem(bookKey)) {
                  localStorage.removeItem(bookKey)
                }
                if (localStorage.getItem(OVERALL_RANKING_COLUMN_STATE_KEY)) {
                  localStorage.removeItem(OVERALL_RANKING_COLUMN_STATE_KEY)
                }

                gridRef.current?.api.resetColumnState()
              },
            }
          }
          return item
        })
      },
    }),
    []
  )

  const handleGridReady = () => {
    if (!gridRef.current) return
    const savedState = localStorage.getItem(columnStateKey)
    if (savedState && gridRef.current.api) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }

  const handleColumnChanged = (event: ColumnEvent) => {
    // Ignore programmatic changes (e.g. applyColumnState on restore) so they
    // don't overwrite the user's saved layout.
    if (isProgrammaticColumnEvent(event.source)) return
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(columnStateKey, JSON.stringify(state))
  }

  const handleSortChanged = (event: SortChangedEvent) => {
    // Ignore programmatic sort changes so a restore/refetch can't persist an
    // empty sort over the user's choice.
    if (isProgrammaticColumnEvent(event.source)) return
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(columnStateKey, JSON.stringify(state))
  }

  const handleColumnResized = (event: ColumnResizedEvent) => {
    // Persist manual drag and the column menu's autosize actions.
    if (!gridRef.current || !isPersistableColumnResize(event)) return

    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(columnStateKey, JSON.stringify(state))
  }

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columns={columns}
      defaultColDef={defaultColDef}
      rowData={rankingList}
      loading={isLoading || isFetching}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onColumnResized={handleColumnResized}
      onSortChanged={handleSortChanged}
      //autoSizeStrategy={autoSizeStrategy}
      onFirstDataRendered={applyLocalStorageColumnState}
      height={height}
    />
  )
}
